import {isPlastyvergTenant} from 'src/modules/tarea/dynamicSubsectionPdfGrouping';
import {
  getMissingRequiredMedicionBlockLabels,
  RequiredMedicionBlock,
} from 'src/modules/tarea/medicionesParcialId23';

export const ID24_MODELO_TITULO_MARKER = 'ID-24';

export const CONTROL_VISUAL_GOMA_TEFLON_OPTIONS = ['INICIO', 'TERMINO'] as const;
export const TIPO_EMBALAJE_OPTIONS = ['SACO', 'BOLSA_FUNDA'] as const;
export const ID24_MICROPERFORADO_OPTIONS = ['Grueso', 'Fino', 'N/A'] as const;
export const ID24_MAQUINA_SELLADORA_OPTIONS = [
  'Valmart',
  'Arvor',
  'Bernardi',
  'Sala Machine',
  'Maqplas',
  'Refiladora Bielloni',
  'Refiladora Reinaflex',
] as const;
export const ID24_COLOR_FUNDA_OPTIONS = ['Natural', 'Amarillo', 'N/A'] as const;
export const ID24_TROQUEL_OPTIONS = ['26*65', '22*85', '20*85', '25*89', 'N/A'] as const;
export const ID24_TIPO_SELLO_OPTIONS = [
  'Fino',
  'Fondo',
  'Reforzado',
  'Tramo',
  'Sello Solapa',
  'Para líquidos',
  'Solo corte',
] as const;
export const ID24_PREPICADO_OPTIONS = ['Si', 'No'] as const;

export type ControlVisualGomaTeflonOption = (typeof CONTROL_VISUAL_GOMA_TEFLON_OPTIONS)[number];
export type TipoEmbalajeOption = (typeof TIPO_EMBALAJE_OPTIONS)[number];

export type MedicionesParcialId24 = {
  troquel?: string | null;
  troquel_cm?: number | string | null;
  cant_perforaciones?: number | string | null;
  microperforado?: string | null;
  tipo_sello?: string | null;
  prepicado?: string | null;
  color_funda?: string | null;
  maquina_selladora?: string | null;
  control_visual_goma_teflon?: ControlVisualGomaTeflonOption[] | null;
  tipo_embalaje?: TipoEmbalajeOption[] | null;
  na_cant_perforaciones?: boolean | null;
  na_prepicado?: boolean | null;
  na_control_visual_goma_teflon?: boolean | null;
  na_tipo_embalaje?: boolean | null;
};

export const emptyMedicionesParcialId24 = (): MedicionesParcialId24 => ({
  troquel: null,
  troquel_cm: null,
  cant_perforaciones: null,
  microperforado: null,
  tipo_sello: null,
  prepicado: null,
  color_funda: null,
  maquina_selladora: null,
  control_visual_goma_teflon: [],
  tipo_embalaje: [],
  na_cant_perforaciones: false,
  na_prepicado: false,
  na_control_visual_goma_teflon: false,
  na_tipo_embalaje: false,
});

export const isId24ModeloTareaTitle = (titulo?: string | null) =>
  String(titulo || '')
    .toUpperCase()
    .includes(ID24_MODELO_TITULO_MARKER);

export const shouldUseMedicionesParcialId24 = (params: {
  tenantId?: string | null;
  modeloTareaTitulo?: string | null;
  primeroDelParcial?: boolean | null;
}) =>
  Boolean(
    isPlastyvergTenant(params.tenantId) &&
      isId24ModeloTareaTitle(params.modeloTareaTitulo) &&
      params.primeroDelParcial,
  );

const normalizeStringArray = <T extends string>(
  value: unknown,
  allowed: readonly T[],
): T[] => {
  if (!Array.isArray(value)) return [];
  const allowedSet = new Set<string>(allowed);
  return value.filter((item): item is T => typeof item === 'string' && allowedSet.has(item));
};

const resolveTroquelValue = (incoming: MedicionesParcialId24) => {
  if (incoming.troquel !== null && incoming.troquel !== undefined && incoming.troquel !== '') {
    return String(incoming.troquel);
  }
  if (incoming.troquel_cm !== null && incoming.troquel_cm !== undefined && incoming.troquel_cm !== '') {
    return String(incoming.troquel_cm);
  }
  return null;
};

export const normalizeMedicionesParcialId24 = (value?: MedicionesParcialId24 | null): MedicionesParcialId24 => {
  const base = emptyMedicionesParcialId24();
  const incoming = value || {};
  return {
    ...base,
    ...incoming,
    troquel: resolveTroquelValue(incoming),
    control_visual_goma_teflon: normalizeStringArray(
      incoming.control_visual_goma_teflon,
      CONTROL_VISUAL_GOMA_TEFLON_OPTIONS,
    ),
    tipo_embalaje: normalizeStringArray(incoming.tipo_embalaje, TIPO_EMBALAJE_OPTIONS),
  };
};

const isId24PrimeroDelParcialTask = (task: any) =>
  task?.primeroDelParcial === true &&
  isId24ModeloTareaTitle(task?.modeloTarea?.titulo || task?.tituloTarea || task?.titulo);

export const findPrimeroDelParcialId24Task = (tasks: any[] = []) =>
  tasks.find(isId24PrimeroDelParcialTask) || null;

export const ID24_REQUIRED_MEDICION_BLOCKS: readonly RequiredMedicionBlock[] = [
  {labelKey: 'entities.tarea.medicionesParcial.maquinaSelladora', keys: ['maquina_selladora']},
  {labelKey: 'entities.tarea.medicionesParcial.tipoSello', keys: ['tipo_sello']},
  {labelKey: 'entities.tarea.medicionesParcial.microperforado', keys: ['microperforado']},
  {labelKey: 'entities.tarea.medicionesParcial.colorFunda', keys: ['color_funda']},
  {labelKey: 'entities.tarea.medicionesParcial.troquel', keys: ['troquel']},
  {
    labelKey: 'entities.tarea.medicionesParcial.cantPerforaciones',
    naKey: 'na_cant_perforaciones',
    keys: ['cant_perforaciones'],
  },
  {labelKey: 'entities.tarea.medicionesParcial.prepicado', naKey: 'na_prepicado', keys: ['prepicado']},
  {
    labelKey: 'entities.tarea.medicionesParcial.controlVisualGomaTeflon',
    naKey: 'na_control_visual_goma_teflon',
    arrayKey: 'control_visual_goma_teflon',
  },
  {
    labelKey: 'entities.tarea.medicionesParcial.tipoEmbalaje',
    naKey: 'na_tipo_embalaje',
    arrayKey: 'tipo_embalaje',
  },
];

export const getMissingRequiredMedicionesParcialId24Labels = (
  mediciones: MedicionesParcialId24 | null | undefined,
  translate: (key: string) => string,
) => getMissingRequiredMedicionBlockLabels(mediciones, ID24_REQUIRED_MEDICION_BLOCKS, translate);

export const hasMedicionesParcialCheckbox = (
  selected: string[] | null | undefined,
  option: string,
) => Array.isArray(selected) && selected.includes(option);
